export interface ThemeColors {
  bg: string;
  bgSecondary: string;
  text: string;
  textMuted: string;
  textSecondary: string;
  card: string;
  sectionBg: string;
  sectionBorder: string;
  cardHover: string;
  cardBorder: string;
  bgAccent: string;
  buttonPrimary: string;
  buttonPrimaryText: string;
  buttonSecondary: string;
  buttonSecondaryText: string;
  inputBg: string;
  inputText: string;
  inputPlaceholder: string;
  selectOption: string;
  border: string;
  borderSubtle: string;
  errorBg: string;
  errorText: string;
  accentBg: string;
  accentText: string;
  iconBg: string;
  iconText: string;
  badge: string;
  // Board pieces shared across components.
  /** A raised panel: ink outline, hard shadow, tile background. */
  panel: string;
  /** A slot inside a panel (list rows, results). */
  row: string;
  /** Same as row, for the current player or selected item. */
  rowActive: string;
  /** Small selectable pill, unselected. */
  chip: string;
  /** Small selectable pill, selected. */
  chipActive: string;
  /** Compact secondary button (Random, Start, Invite...). */
  buttonSmall: string;
  /** Compact danger button (Give Up, End Game). */
  buttonDanger: string;
  /** Text field wrapper. */
  field: string;
  /** Divider line inside a panel. */
  divider: string;
  /** "You" marker next to the current player. */
  youBadge: string;
  // Legacy tokens for backward compatibility
  badgePending: string;
}

// Board design. zinc/gray here are the remapped board palettes from
// index.css: zinc is the board at night, gray the board by day.
const BOARD_PIECE = "border-[3px] border-ink shadow-board";
const BOARD_BUTTON = "border-[3px] border-ink shadow-board-sm press font-display";
const CHIP =
  "rounded-full border-2 border-ink px-3 py-1 font-display text-xs cursor-pointer transition-[transform,background-color] duration-150 disabled:cursor-not-allowed disabled:opacity-50";
const SMALL_BUTTON =
  "inline-flex items-center justify-center gap-1.5 rounded-lg border-2 border-ink px-3 py-1.5 font-display text-xs shadow-board-sm press cursor-pointer disabled:cursor-not-allowed disabled:opacity-50";
const YOU_BADGE =
  "rounded-md border-2 border-ink bg-cream px-1.5 py-0.5 font-display text-[10px] leading-none text-ink";
const FIELD =
  "rounded-xl border-[3px] border-ink focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-gold";

const DARK_THEME: ThemeColors = {
  bg: "bg-zinc-950",
  bgSecondary: "bg-zinc-900",
  text: "text-white",
  textMuted: "text-zinc-500",
  textSecondary: "text-zinc-400",
  sectionBg: "md:bg-zinc-900",
  sectionBorder: "md:border-[3px] md:border-ink md:shadow-board",
  card: "bg-zinc-900 border-ink",
  cardHover: "transition-transform hover:-translate-y-1 hover:bg-zinc-800",
  cardBorder: BOARD_PIECE,
  bgAccent: "bg-gold",
  buttonPrimary: `bg-gold hover:bg-[#ffd566] ${BOARD_BUTTON}`,
  buttonPrimaryText: "text-ink",
  buttonSecondary: `bg-zinc-900 hover:bg-zinc-800 ${BOARD_BUTTON}`,
  buttonSecondaryText: "text-white",
  inputBg: "bg-transparent",
  inputText: "text-white",
  inputPlaceholder: "placeholder:text-zinc-600",
  selectOption: "bg-zinc-900 text-white",
  border: "border-ink",
  borderSubtle: "border-ink/60",
  errorBg: "bg-duel/15",
  errorText: "text-[#ffb3ba]",
  accentBg: "bg-gold",
  accentText: "text-ink",
  iconBg: "bg-ink",
  iconText: "text-gold",
  badge: "bg-black border-2 border-ink text-zinc-300",
  panel: `rounded-2xl ${BOARD_PIECE} bg-zinc-900`,
  row: "rounded-xl border-2 border-ink bg-black",
  rowActive: "rounded-xl border-2 border-ink bg-zinc-800",
  chip: `${CHIP} bg-black text-zinc-300 hover:bg-zinc-800 hover:text-white`,
  chipActive: `${CHIP} bg-gold text-ink shadow-board-sm -translate-y-0.5`,
  buttonSmall: `${SMALL_BUTTON} bg-zinc-900 text-white hover:bg-zinc-800`,
  buttonDanger: `${SMALL_BUTTON} bg-duel text-ink hover:bg-[#ff6b79]`,
  field: `${FIELD} bg-black text-white`,
  divider: "border-ink/60",
  youBadge: YOU_BADGE,
  // Legacy tokens
  badgePending: "bg-black text-zinc-400 border-ink",
};

const LIGHT_THEME: ThemeColors = {
  bg: "bg-gray-50",
  bgSecondary: "bg-white",
  text: "text-gray-900",
  textMuted: "text-gray-500",
  textSecondary: "text-gray-600",
  sectionBg: "md:bg-white",
  sectionBorder: "md:border-[3px] md:border-ink md:shadow-board",
  card: "bg-white border-ink",
  cardHover: "transition-transform hover:-translate-y-1 hover:bg-gray-50",
  cardBorder: BOARD_PIECE,
  bgAccent: "bg-gold",
  buttonPrimary: `bg-gold hover:bg-[#ffd566] ${BOARD_BUTTON}`,
  buttonPrimaryText: "text-ink",
  buttonSecondary: `bg-white hover:bg-gray-100 ${BOARD_BUTTON}`,
  buttonSecondaryText: "text-gray-900",
  inputBg: "bg-transparent",
  inputText: "text-gray-900",
  inputPlaceholder: "placeholder:text-gray-400",
  selectOption: "bg-white text-gray-900",
  border: "border-ink",
  borderSubtle: "border-ink/25",
  errorBg: "bg-duel/15",
  errorText: "text-[#b3101f]",
  accentBg: "bg-gold",
  accentText: "text-ink",
  iconBg: "bg-ink",
  iconText: "text-gold",
  badge: "bg-white border-2 border-ink text-gray-700",
  panel: `rounded-2xl ${BOARD_PIECE} bg-white`,
  row: "rounded-xl border-2 border-ink bg-gray-50",
  rowActive: "rounded-xl border-2 border-ink bg-gray-100",
  chip: `${CHIP} bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900`,
  chipActive: `${CHIP} bg-gold text-ink shadow-board-sm -translate-y-0.5`,
  buttonSmall: `${SMALL_BUTTON} bg-white text-gray-900 hover:bg-gray-100`,
  buttonDanger: `${SMALL_BUTTON} bg-duel text-ink hover:bg-[#ff6b79]`,
  field: `${FIELD} bg-white text-gray-900`,
  divider: "border-ink/25",
  youBadge: YOU_BADGE,
  // Legacy tokens
  badgePending: "bg-gray-100 text-gray-600 border-ink",
};

export function getThemeColors(isDark: boolean): ThemeColors {
  return isDark ? DARK_THEME : LIGHT_THEME;
}
